import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText, Search, Sparkles, Calendar, AlertTriangle, FolderOpen,
  Download, Eye, MessageSquare, Filter,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { KpiCard } from '@/components/KpiCard';
import { useAuth } from '@/contexts/AuthContext';
import {
  usePrograms, programStatus, CATEGORY_LABELS, CATEGORY_ORDER,
  getSignedUrl, type ProgramCategory, type Program,
} from '@/data/hooks/usePrograms';
import { UploadProgramDialog } from '@/components/programs/UploadProgramDialog';
import { ExpiringRail } from '@/components/programs/ExpiringRail';
import { countdownLabel, urgencyTier } from '@/lib/programStatus';
import { toast } from 'sonner';

function formatDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function StatusPill({ p }: { p: Program }) {
  const s = programStatus(p);
  if (s === 'expired') return <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-red-500/15 text-red-400">Expired</span>;
  if (s === 'expiring_soon') return <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-yellow-500/15 text-yellow-400">Expiring</span>;
  return <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-green-500/15 text-green-400">Active</span>;
}

async function openFile(filePath: string, download = false) {
  const { data, error } = await getSignedUrl(filePath);
  if (error || !data) { toast.error('Could not open file'); return; }
  if (download) {
    const a = document.createElement('a');
    a.href = data.signedUrl; a.download = ''; a.target = '_blank'; a.click();
  } else {
    window.open(data.signedUrl, '_blank', 'noopener');
  }
}

export default function Programs() {
  const { role, mode } = useAuth();
  const isManager = role === 'manager' || role === 'admin';
  const { programs, loading, refresh } = usePrograms();

  const [query, setQuery] = useState('');
  const [catFilter, setCatFilter] = useState<ProgramCategory | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'expiring_soon' | 'this_month'>('all');

  const stats = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    let active = 0, expiring = 0, newThisMonth = 0;
    for (const p of programs) {
      const s = programStatus(p);
      if (s === 'active') active++;
      if (s === 'expiring_soon') expiring++;
      if (new Date(p.created_at) >= monthStart) newThisMonth++;
    }
    return { active, expiring, newThisMonth, total: programs.length };
  }, [programs]);

  const byCategory = useMemo(() => {
    const map = new Map<ProgramCategory, Program[]>();
    for (const c of CATEGORY_ORDER) map.set(c, []);
    for (const p of programs) map.get(p.category)?.push(p);
    return map;
  }, [programs]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const matched = programs.filter(p => {
      if (catFilter !== 'all' && p.category !== catFilter) return false;
      const s = programStatus(p);
      if (statusFilter === 'active' && s === 'expired') return false;
      if (statusFilter === 'expiring_soon' && s !== 'expiring_soon') return false;
      if (statusFilter === 'this_month' && new Date(p.created_at) < monthStart) return false;
      if (!q) return true;
      const hay = [p.title, p.summary, ...(p.keywords ?? []), CATEGORY_LABELS[p.category]].join(' ').toLowerCase();
      return hay.includes(q);
    });
    // Pin expiring programs to the top, then by expiry date asc, then most recently updated
    const tierWeight = (p: Program) => {
      const t = urgencyTier(p);
      if (t === 'final') return 0;
      if (t === 'soon') return 1;
      if (t === 'ok') return 2;
      if (t === 'none') return 3;
      return 4; // expired
    };
    return [...matched].sort((a, b) => {
      const w = tierWeight(a) - tierWeight(b);
      if (w !== 0) return w;
      const aExp = a.expiry_date ?? '9999-12-31';
      const bExp = b.expiry_date ?? '9999-12-31';
      if (aExp !== bExp) return aExp.localeCompare(bExp);
      return (b.updated_at ?? '').localeCompare(a.updated_at ?? '');
    });
  }, [programs, query, catFilter, statusFilter]);

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="rounded-2xl border border-border/60 bg-gradient-to-br from-card via-card/80 to-background p-6 backdrop-blur-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Programs Hub</h1>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Quick access to current incentives, finance programs, lease rates, rebates, and sales updates.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {isManager && <UploadProgramDialog onUploaded={refresh} />}
            <Button variant="outline" className="gap-2" onClick={() => document.getElementById('search-input')?.focus()}>
              <Search className="h-4 w-4" />Search Programs
            </Button>
            <Button variant="outline" className="gap-2" onClick={() => document.getElementById('programs-table')?.scrollIntoView({ behavior: 'smooth' })}>
              <Sparkles className="h-4 w-4" />Latest Updates
            </Button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="Active Programs" value={stats.active} icon={FileText} color="text-green-400" />
        <KpiCard label="Expiring Soon" value={stats.expiring} icon={AlertTriangle} color="text-yellow-400" />
        <KpiCard label="New This Month" value={stats.newThisMonth} icon={Sparkles} color="text-primary" />
        <KpiCard label="PDFs Uploaded" value={stats.total} icon={FolderOpen} color="text-blue-400" />
      </div>

      {/* Urgency rail — pinned expiring programs */}
      <ExpiringRail programs={programs} />

      {/* Categories */}
      <div>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Categories</h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {CATEGORY_ORDER.map(c => {
            const list = byCategory.get(c) ?? [];
            const latest = list[0];
            return (
              <button
                key={c}
                onClick={() => { setCatFilter(c); document.getElementById('programs-table')?.scrollIntoView({ behavior: 'smooth' }); }}
                className={`group relative rounded-xl border p-4 text-left backdrop-blur-sm transition hover:border-primary/40 hover:bg-card/90 ${
                  catFilter === c ? 'border-primary/60 bg-card' : 'border-border/60 bg-card/60'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-sm font-semibold">{CATEGORY_LABELS[c]}</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {list.length} {list.length === 1 ? 'file' : 'files'}
                    </div>
                  </div>
                  <FolderOpen className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                </div>
                {latest && (
                  <div className="mt-3 truncate text-xs text-muted-foreground">
                    Latest: {formatDate(latest.updated_at)}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Filters + Table */}
      <Card id="programs-table" className="border-border/60 bg-card/80 backdrop-blur-sm">
        <div className="flex flex-col gap-3 border-b border-border/60 p-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-1 items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              id="search-input"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search title, summary, or keywords…"
              className="h-9 max-w-md"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <select
              value={catFilter}
              onChange={e => setCatFilter(e.target.value as any)}
              className="rounded border border-border bg-muted/30 px-2 py-1 text-xs"
            >
              <option value="all">All categories</option>
              {CATEGORY_ORDER.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
            </select>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as any)}
              className="rounded border border-border bg-muted/30 px-2 py-1 text-xs"
            >
              <option value="all">All status</option>
              <option value="active">Active only</option>
              <option value="expiring_soon">Expiring soon</option>
              <option value="this_month">Uploaded this month</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/20">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-medium">Program</th>
                <th className="px-3 py-3 font-medium">Category</th>
                <th className="px-3 py-3 font-medium">Uploaded By</th>
                <th className="px-3 py-3 font-medium">Effective</th>
                <th className="px-3 py-3 font-medium">Expires</th>
                <th className="px-3 py-3 font-medium">Status</th>
                <th className="px-3 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                  No programs match these filters{isManager ? ' — upload your first PDF.' : '.'}
                </td></tr>
              )}
              {filtered.map(p => {
                const tier = urgencyTier(p);
                const rowClass =
                  tier === 'final' ? 'border-l-2 border-l-red-500/60 bg-red-500/[0.03]'
                  : tier === 'soon' ? 'border-l-2 border-l-yellow-500/60 bg-yellow-500/[0.03]'
                  : '';
                const hasPlaybook = !!(p.best_for || p.talk_track || p.objection_handling || p.urgency_hook);
                return (
                <tr key={p.id} className={`border-t border-border/40 hover:bg-muted/20 ${rowClass}`}>
                  <td className="px-4 py-3">
                    <Link to={`/programs/${p.id}`} className="font-medium text-foreground hover:text-primary">
                      {p.title}
                    </Link>
                    {p.processing_status === 'pending' && (
                      <span className="ml-2 text-[10px] font-bold uppercase text-yellow-400">Processing…</span>
                    )}
                    {hasPlaybook && (
                      <span className="ml-2 inline-flex items-center gap-1 rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-primary">
                        Playbook
                      </span>
                    )}
                    {p.summary && (
                      <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{p.summary}</div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-xs text-muted-foreground">{CATEGORY_LABELS[p.category]}</td>
                  <td className="px-3 py-3 text-xs text-muted-foreground">{p.uploaded_by_name ?? '—'}</td>
                  <td className="px-3 py-3 text-xs text-muted-foreground">{formatDate(p.effective_date)}</td>
                  <td className="px-3 py-3 text-xs">
                    <div className="text-muted-foreground">{formatDate(p.expiry_date)}</div>
                    {(tier === 'soon' || tier === 'final') && (
                      <div className={`text-[11px] font-semibold ${tier === 'final' ? 'text-red-400' : 'text-yellow-400'}`}>
                        {countdownLabel(p)}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3"><StatusPill p={p} /></td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" title="View" onClick={() => openFile(p.file_path)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" title="Download" onClick={() => openFile(p.file_path, true)}>
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button asChild size="icon" variant="ghost" className="h-8 w-8" title="AI Summary & Search">
                        <Link to={`/programs/${p.id}`}><MessageSquare className="h-4 w-4" /></Link>
                      </Button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}